// ---------------------------------------------------------------------------
// Is the bill covered? The ONE rule, shared with the server.
//
// The bill is a whole number of lira - exact, authoritative. The till also
// quotes it in dollars, rounded to the cent, and a customer may pay in
// either. So:
//
//   * LIRA PAYS LIRA EXACTLY. What is tendered in lira is subtracted from
//     the lira bill as it is; a shortfall of a single pound is a shortfall.
//   * DOLLARS PAY THE CENT-QUOTED PRICE OF WHAT IS LEFT. The lira still owed
//     after the lira tenders is priced in dollars the same way the till
//     quotes every dollar figure - divided by the rate, rounded to the cent
//     - and the dollar tenders must cover that quote.
//
// Nothing here starts from a rounded dollar total and converts it back to
// lira, which is where a phantom cent used to come from: a bill quoted at
// $9.62 whose lines, rounded one by one, added up to $9.63. The server
// applies the identical rule (backend/src/utils/money.ts), so what this
// says is covered, the server records - and what it says is short, the
// server refuses.
// ---------------------------------------------------------------------------

import Decimal from 'decimal.js';
import { toDecimal } from './format';

export interface TenderLine {
  currency: 'USD' | 'LBP';
  amount: string | number | Decimal;
}

export interface Settlement {
  covered: boolean;
  /// Lira tendered, and dollars tendered, as they were typed.
  lbpPaid: Decimal;
  usdPaid: Decimal;
  /// Lira still owed after the lira tenders, and its cent-quoted dollar
  /// price. Both zero once covered.
  remainingLbp: Decimal;
  remainingUsd: Decimal;
  /// What the customer gets back, once covered. Zero otherwise.
  changeUsd: Decimal;
  changeLbp: Decimal;
}

export function settleInLira(totalLbp: string | number | Decimal, rate: string | number | Decimal, tenders: TenderLine[]): Settlement {
  const bill = toDecimal(totalLbp).toDecimalPlaces(0);
  const usdToLbpRate = toDecimal(rate);
  const lbpPaid = tenders.filter((row) => row.currency === 'LBP').reduce((sum, row) => sum.plus(toDecimal(row.amount).toDecimalPlaces(0)), new Decimal(0));
  const usdPaid = tenders.filter((row) => row.currency === 'USD').reduce((sum, row) => sum.plus(toDecimal(row.amount).toDecimalPlaces(2)), new Decimal(0));
  const zero = new Decimal(0);

  const liraLeft = bill.minus(lbpPaid);
  if (liraLeft.lessThanOrEqualTo(0)) {
    // Lira alone covers it. Dollars tendered on top are change in full.
    const changeLbp = liraLeft.negated().plus(usdPaid.times(usdToLbpRate).toDecimalPlaces(0));
    return { covered: true, lbpPaid, usdPaid, remainingLbp: zero, remainingUsd: zero, changeUsd: changeLbp.dividedBy(usdToLbpRate).toDecimalPlaces(2), changeLbp };
  }

  const quotedUsd = liraLeft.dividedBy(usdToLbpRate).toDecimalPlaces(2);
  if (usdPaid.greaterThan(0) && usdPaid.greaterThanOrEqualTo(quotedUsd)) {
    const changeUsd = usdPaid.minus(quotedUsd);
    return { covered: true, lbpPaid, usdPaid, remainingLbp: zero, remainingUsd: zero, changeUsd, changeLbp: changeUsd.times(usdToLbpRate).toDecimalPlaces(0) };
  }

  const remainingLbp = liraLeft.minus(usdPaid.times(usdToLbpRate).toDecimalPlaces(0));
  return {
    covered: false,
    lbpPaid,
    usdPaid,
    remainingLbp: Decimal.max(remainingLbp, 0),
    remainingUsd: Decimal.max(quotedUsd.minus(usdPaid), 0),
    changeUsd: zero,
    changeLbp: zero,
  };
}
